import { ADVOCATE_ROUTES } from 'consts';
import { store } from 'store';

import { logAuthRedirectDecision } from './authRedirectObservability';

// Only the no-login NANL sub-paths. /collection-link/get-story-link/
// is checkAuth-gated (see web-be/app/routes/advocate.ts) and must
// keep the refresh-and-retry flow.
const PUBLIC_NANL_PATH_FRAGMENTS = [
  '/advocate/collection-link/story/',
  '/advocate/collection-link/response/',
  '/advocate/collection-link/reset-session/',
  '/advocate/collection-link/generate-token/',
  '/advocate/collection-link/get-cookie',
  '/advocate/collection-link/send-login-link/',
  '/advocate/nanl/',
];

/**
 * Whether a 401 must be handed straight back to the caller instead of going
 * through the interceptor's refresh-and-retry / redirect-to-login handling.
 */
export const shouldSkipAuthRedirect = (requestUrl: string): boolean => {
  // NANL public endpoints handle their own errors — never refresh tokens or
  // redirect to login, both behaviours break the no-login advocate flow.
  if (PUBLIC_NANL_PATH_FRAGMENTS.some((p) => requestUrl.includes(p))) {
    return true;
  }

  const pathname = window.location.pathname;

  // IPS-711 — TEMPORARY targeted hotfix. While the visitor is on the public
  // collection-link page, a 401 from a background bootstrap call
  // (getOnboard/getSharing/getAllPlatform fired by a stale token) must NOT
  // bounce them to the employer login wall. Returning here also skips the
  // refresh-and-retry path, whose failure branch redirects too.
  //
  // PERMANENT FIX: gate the interceptor's redirect on whether the *current
  // route* is public/no-login (a single allow-list of public routes), rather
  // than hard-coding this one path here and the endpoint allow-list above —
  // the redirect is correct on protected pages, wrong on public ones.
  if (pathname.startsWith('/story/collection-link/')) {
    const auth = store.getState().auth;
    logAuthRedirectDecision({
      action: 'redirect_suppressed',
      routeType: 'public_no_login',
      currentUrl: window.location.href,
      failedRequestUrl: requestUrl,
      hadAccessToken: !!auth?.token,
      hadRefreshToken: !!auth?.refresh_token,
    });
    return true;
  }

  // A 401 from the login endpoint while the advocate login page is open means
  // "wrong email/password", not an expired session. Falling through would wipe
  // auth state (losing login.error, so the inline Alert never renders, and the
  // CSRF session token) and then redirect to the employer login page, because
  // the redirect guard only recognises that one login route. Scoped to the
  // advocate login page on purpose: the employer login flow is left untouched.
  const isAdvocateLoginPage =
    pathname === ADVOCATE_ROUTES.LOGIN ||
    pathname === `${ADVOCATE_ROUTES.LOGIN}/`;
  if (isAdvocateLoginPage && requestUrl.includes('/auth/login')) {
    return true;
  }

  return false;
};
